import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { IconButton, MenuList } from "@mui/material";
import clsx from "clsx";
import React, { useContext, useEffect, useRef, useState } from "react";
import { smetaModals, SmetaModalsButtonsContext, SmetaModalsIdContext } from "../../Tools/modalManager";
import { Prefixes } from "../../Tools/ProjectX";
import { escapeRegExp, formEdit, getStyleWindow } from "../../Tools/Tools";
import VirtualList, { EventDataInfo } from "../../Tools/VirtualList/VirtualList";
import dialogStyles from "./Components/dialogStyles.module.css";
import DialogCreateObj from "./DialogCreateObj";

interface Data {
  setValue: (data: { text: string; id?: string }) => void;
  setInputValue: (data?: string) => void;
  tag?: string | number;
}

interface DialogSetObjTextProps {
  list: EventDataInfo[];
  record?: HTMLUnknownElement;
  name?: string;
  data?: Data;
  RequestID?: string;
  CheckState?: string;
  from?: string;
  hiddenButton?: boolean;
  Buttons?: string | number;
  func?: (data: any) => any;
  onEdit?: (data: any) => void | Promise<void>;
  onClose?: () => void;
  createNew?: {
    Text: string;
    CLSID: string;
    Label?: string;
    Title?: string;
    Groups?: { Module: string; ObjType: string };
    ControlHeight?: string;
    NoFormSaver?: string;
    BorderStyle?: string;
  };
  module?: string | Prefixes;
  objType?: string | number;
  resumeAfterEdit?: boolean;
}

const isServerSelected = (item: EventDataInfo) =>
  item.selected === true || item.selected === 1 || item.selected === "1" || item.selected === "true";

const getInitialSelected = (list: EventDataInfo[], selectedId?: string | number) =>
  list.find(isServerSelected) ??
  (selectedId !== undefined ? list.find((item) => String(item.id) === String(selectedId)) : undefined) ??
  list[0] ??
  null;

/**
 * Контент диалога Выбор элемента из списка
 *
 * Поля компонента:
 *    1. [sortRecords, setSortRecords] - отсортированные записи
 *    2. [selected, setSelected] - выбранный элемент
 *    3. records - массив записей
 *    4. inputFilter - ссылка на фильтр
 *
 * @param {Object} [props] объект с данными для компонента подразделений
 * @param {string} [props.Buttons] настройка видимых кнопок
 * @param {Object} [props.list] массив записей для таблицы
 * @param {string} [props.name] данные форм
 * @param {string} [props.record] данные записи для параметров
 * @param {string} [props.data] дополнительные данные
 * @param {string} [props.RequestID] id в цепи запросов
 * @param {string} [props.CheckState] данные для параметров
 * @param {string} [props.from] данные откуда был вызов
 * @param {string} [props.hiddenButton] скрытие кнопок
 * @param {string} [props.onEdit] функция на выбор
 * @param {string} [props.func] функция для завершения цепи запросов
 * @param {string} [props.onClose] функция для отмены
 * @param {Object} [props.createNew] информация нужная для диалога создания нового объекта
 * @param {string | Prefixes} [props.module] модуль для диалога создания нового объекта
 * @param {string | number} [props.objType] тип объекта для диалога создания нового объекта
 *
 * @returns Возвращает контент диалога для выбора элемента из списка
 */

// eslint-disable-next-line @typescript-eslint/naming-convention
export default function DialogSetObjText(props: DialogSetObjTextProps) {
  const {
    list,
    record,
    name,
    data,
    RequestID: requestId,
    CheckState: checkState,
    from,
    Buttons: buttons,
    hiddenButton,
    onEdit,
    func,
    onClose,
    createNew,
    module,
    objType,
    resumeAfterEdit,
  } = props;

  const selectedId = data?.tag ?? (record instanceof HTMLElement ? record.dataset.objref : undefined);
  const [sortRecords, setSortRecords] = useState<EventDataInfo[]>([]);
  const [selected, setSelected] = useState<EventDataInfo | null>(() => getInitialSelected(list, selectedId));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const records = useRef<EventDataInfo[]>([]);
  const inputFilter = useRef<HTMLInputElement>(null);
  const isSubmittingRef = useRef(false);

  const setButtons = useContext(SmetaModalsButtonsContext);
  const smetaModalId = useContext(SmetaModalsIdContext);

  /**
   * Инициализация таблицы
   */
  const initialization = () => {
    records.current = list;
    setSortRecords(records.current);
  };
  useEffect(initialization, []);

  useEffect(() => {
    if (hiddenButton) return;
    const modalButtons: {
      [key: number]: { label: string; onClick?: () => void | Promise<void>; disabled?: boolean };
    } = {};
    if (!buttons || Number(buttons) & 1) {
      modalButtons[1] = {
        label: "Установить",
        onClick: onSelect,
        disabled: selected === null || isSubmitting,
      };
      modalButtons[2] = { label: "Закрыть", onClick: onCancel };
    }
    setButtons?.(modalButtons);
  }, [setButtons, selected, isSubmitting]);

  /**
   * Выбирает элемент из списка и закрывает окно
   *
   * @param item - выбранный элемент
   */
  const onSelectFromList = async (item?: EventDataInfo | null) => {
    if (!item || isSubmittingRef.current) return false;

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      if (onEdit) {
        await onEdit({
          record: record,
          textchanged: "0",
          name: name,
          setValue: data?.setValue,
          setInputValue: data?.setInputValue,
          ...data,
          proof: false,
          value: item.text,
          tag: item.id,
          code: item.code,
          ...item,
          requestId: requestId,
          CheckState: checkState,
        });
        if (resumeAfterEdit && requestId) {
          await formEdit("1", func, {}, requestId, from);
        }
        return true;
      }

      if (requestId) {
        await formEdit("1", func, { Name: "Index", Value: item.id }, requestId, from);
      }
      return true;
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const selectAndClose = async (item?: EventDataInfo | null) => {
    if (await onSelectFromList(item)) {
      await smetaModals.close(smetaModalId);
    }
  };

  /**
   * Фильтрует записи по значению фильтра
   *
   * @param reg - значение фильтра
   */
  const filter = (reg: string) => {
    const regexp = new RegExp(escapeRegExp(reg).toLowerCase());
    setSortRecords(
      records.current.filter((item: EventDataInfo) => {
        if (item.text) {
          if (item.text.toLowerCase().match(regexp)) return item;
        }
        return false;
      }),
    );
  };

  /**
   * Событие при клике на Выбор
   */
  const onSelect = async () => {
    await onSelectFromList(selected);
  };

  /**
   * Событие при клике на Отмена
   */
  const onCancel = () => {
    onClose?.();
  };

  /**
   * Событие при клике на кнопку фильтрации
   */
  const onFilterClick = () => {
    if (inputFilter.current) filter(inputFilter.current.value);
  };

  /**
   * Событие при клике на кнопку "Создать новый объект"
   */
  const onCreateNewClick = () => {
    if (!createNew || !module || !objType) return;
    smetaModals.open(
      <DialogCreateObj
        Module={module}
        ObjType={objType}
        inputNameObj={createNew.Text}
        spanText={createNew.Label}
        CLSID={createNew.CLSID}
        groups={createNew.Groups}
        onEdit={async (data) => {
          if (!data?.tag || !data?.value) return;
          const item = { id: data.tag, text: data.value };
          const insertSorted = (list: EventDataInfo[], newItem: { id: string; text: string }) => {
            const newList = [...list];
            const index = newList.findIndex((el) => newItem.text.toLowerCase() <= el.text.toLowerCase());
            if (index === -1) {
              newList.push(newItem);
            } else {
              newList.splice(index, 0, newItem);
            }
            return newList;
          };
          records.current = insertSorted(records.current, item);
          setSortRecords((prevList) => insertSorted(prevList, item));
          setSelected(item);
        }}
      />,
      {
        Title: createNew.Title || "Создать новый объект",
        style: getStyleWindow(`${createNew.CLSID}${createNew.Groups ? "Group" : ""}`),
        hiddenButton: true,
        blockMaximize: true,
        blockSaveLocation: true,
      },
    );
  };

  /**
   * Событие при нажатии клавиши Enter при фильтрации
   *
   * @param ev - событие onKeyDown
   */
  const onKeyDownFilter = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === "Enter") {
      const target = ev.target as HTMLInputElement;
      filter(target.value);
    }
  };

  /**
   * Событие выбора элемента при клике на элемент таблицы
   *
   * @param data - данные об элементе
   */
  const onClickTable = (data?: EventDataInfo) => {
    if (data) setSelected(data);
  };

  /**
   * Событие при нажатии клавиши Enter при выборе элементов из таблицы
   *
   * @param data - данные об элементе
   * @param ev - событие onKeyDown
   */
  const onKeyDown = async (data?: EventDataInfo, ev?: React.KeyboardEvent) => {
    if (data && ev?.key === "Enter") {
      ev.preventDefault();
      ev.stopPropagation();
      await selectAndClose(data);
    }
  };

  /**
   * Событие при двойном клике по элементу таблицы
   *
   * @param data - данные об элементе
   */
  const onDoubleClickTable = async (data?: EventDataInfo) => {
    if (data) await selectAndClose(data);
  };

  return (
    <div className={clsx(dialogStyles.mainDialogBlock)}>
      <div className={clsx(dialogStyles.fullDialogFlexContainer, dialogStyles.dialogSetObjectTextMainContainer)}>
        <div className={clsx(dialogStyles.dialogSetObjectTextTableContainer)}>
          <MenuList tabIndex={-1}>
            <VirtualList
              items={sortRecords}
              selected={selected ?? undefined}
              noFocus={selected === null}
              onKeyDown={onKeyDown}
              onActiveItemChange={onClickTable}
              onDoubleClick={onDoubleClickTable}
              onClick={onClickTable}
            />
          </MenuList>
        </div>
        <div className={clsx(dialogStyles.dialogSetObjectTextSearchContainer)}>
          <IconButton onClick={onFilterClick} className={clsx(dialogStyles.dialogSetObjectTextIconButton)}>
            <SearchOutlinedIcon />
          </IconButton>
          <input
            ref={inputFilter}
            className={clsx(dialogStyles.dialogSetObjectTextSearchInput)}
            onKeyDown={onKeyDownFilter}
            title="Фильтр"
          />
          {createNew && (
            <IconButton
              title={createNew.Title ?? "Создать новый объект"}
              onClick={onCreateNewClick}
              className={clsx(dialogStyles.dialogSetObjectTextIconButton)}
            >
              <AddCircleOutlineIcon />
            </IconButton>
          )}
        </div>
      </div>
    </div>
  );
}
